import { NoRecordFound, PageLoader, Iconify, Button, ButtonVariant } from "@repo/design-system/components/ui";
import { ErrorCode } from "@gyandiksha/shared-ui/services";
import PropTypes from "prop-types";
import "./DataStateHandler.scss";

const DEFAULT_ERROR_TITLE = "Oops! Something Went Wrong";
const DEFAULT_ERROR_MESSAGE = "An unexpected error occurred. Please try again later.";

export const DataStateHandler = ({
    isLoading,
    isFetching,
    data,
    noDataMessage = "No records found",
    children,
    loaderClass = "dataset-handler",
    isEnabledImage = true,
    isEnabledIcon,
    isError = false,
    error,
    errorCode,
    errorTitle,
    errorMessage,
    errorIcon = "lucide:triangle-alert",
    isEnabledErrorIcon = true,
    errorClassName = "",
    onRetry
}) => {

    const hasData = Array.isArray(data) ? data.length > 0
        : data && typeof data === "object" ? Object.keys(data).length > 0 : false;


    // Show loader while API is in progress
    if (isLoading || isFetching) {
        return <PageLoader loaderClass={loaderClass} />;
    }

    // Show error state if api failed
    if (isError) {
        // Status code wise title / message, explicit props take priority
        const statusError = ErrorCode[errorCode ?? error?.code ?? error?.status];
        const title = errorTitle || statusError?.title || DEFAULT_ERROR_TITLE;
        const message = errorMessage || statusError?.description || DEFAULT_ERROR_MESSAGE;

        return (
            <div className={`data-state-error ${errorClassName}`}>
                {isEnabledErrorIcon && (
                    <div className="error-icon-container">
                        <Iconify icon={errorIcon} />
                    </div>
                )}
                <h5 className="error-title">{title}</h5>
                <p className="error-message">{message}</p>
                {onRetry && (
                    <Button variant={ButtonVariant.OUTLINE} onClick={onRetry}
                        leftIcon="lucide:refresh-cw" className="error-retry-btn">
                        Retry
                    </Button>
                )}
            </div>
        );
    }

    // Show no records only after API completes
    if (!hasData) {
        return (
            <NoRecordFound
                message={noDataMessage}
                isEnabledImage={isEnabledImage}
                isEnabledIcon={isEnabledIcon}
            />
        );
    }

    return <>{children}</>;
};

DataStateHandler.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    data: PropTypes.array,
    noDataMessage: PropTypes.string,
    children: PropTypes.node.isRequired,
    loaderClass: PropTypes.string,
    isEnabledImage: PropTypes.bool,
    isEnabledIcon: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.object,
    errorCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    errorTitle: PropTypes.string,
    errorMessage: PropTypes.string,
    errorIcon: PropTypes.string,
    isEnabledErrorIcon: PropTypes.bool,
    errorClassName: PropTypes.string,
    onRetry: PropTypes.func
};

